import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import GradientStrip from "@/components/GradientStrip";
import { api } from "@/lib/config";
import { useT, useLanguage } from "@/lib/i18n";
import { CheckCircle2, AlertTriangle, XCircle, RefreshCcw, Activity } from "lucide-react";

const LABELS = {
  fr: {
    api: "Site web & API",
    database: "Base de données",
    email: "Emails (formulaires de contact)",
  },
  en: {
    api: "Website & API",
    database: "Database",
    email: "Emails (contact forms)",
  },
};

export default function Status() {
  const t = useT();
  const { lang } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastCheck, setLastCheck] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/status");
      setData(data);
      setLastCheck(new Date());
    } catch {
      setData({ status: "down", checks: { api: "down" } });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000); // refresh every minute
    return () => clearInterval(id);
  }, []);

  const overall = data?.status || (loading ? "loading" : "down");
  const overallColor = overall === "operational" ? "#9DBC1D" : overall === "degraded" ? "#F3A114" : "#E62C86";
  const overallLabel = {
    operational: lang === "en" ? "All systems operational" : "Tous les systèmes opérationnels",
    degraded: lang === "en" ? "Partial degradation" : "Dégradation partielle",
    down: lang === "en" ? "Service interrupted" : "Service interrompu",
    loading: lang === "en" ? "Checking…" : "Vérification…",
  }[overall];

  const renderCheckIcon = (state) => {
    if (state === "ok") return <CheckCircle2 size={22} className="text-accent-lime" />;
    if (state === "not_configured") return <AlertTriangle size={22} className="text-accent-orange" />;
    return <XCircle size={22} className="text-accent-pink" />;
  };

  const renderCheckLabel = (state) => {
    if (state === "ok") return lang === "en" ? "Operational" : "Opérationnel";
    if (state === "not_configured") return lang === "en" ? "Not configured" : "Non configuré";
    if (state === "down") return lang === "en" ? "Down" : "Indisponible";
    return state;
  };

  const fmtTime = (d) => d?.toLocaleTimeString(lang === "en" ? "en-US" : "fr-FR");

  return (
    <Layout headerVariant="dark">
      <section data-testid="status-hero" className="bg-brand-deep text-white pt-40 md:pt-48 pb-20">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">
          <p className="section-num text-accent-pink fade-up">{lang === "en" ? "System status" : "État des services"}</p>
          <h1 className="display text-5xl md:text-7xl leading-[0.95] mt-8 fade-up-2">
            {lang === "en" ? "Status" : "État du système."}
          </h1>
          <p className="text-white/90 text-lg leading-relaxed mt-8 max-w-2xl fade-up-3">
            {lang === "en"
              ? "Live status of the Wedo Coaching website and connected services. Refreshed every minute."
              : "État en direct du site Wedo Coaching et des services connectés. Actualisé toutes les minutes."}
          </p>
        </div>
        <GradientStrip />
      </section>

      <section className="bg-paper py-20 md:py-28">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">

          {/* Global status banner */}
          <div
            data-testid="status-banner"
            className="border-2 p-8 md:p-10 flex items-center gap-6"
            style={{ borderColor: overallColor, background: `${overallColor}10` }}
          >
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center shrink-0"
              style={{ background: overallColor }}
            >
              <Activity size={28} className="text-brand-deep" strokeWidth={2.4} />
            </div>
            <div className="flex-1">
              <p className="text-[12px] uppercase tracking-[0.3em]" style={{ color: overallColor }}>
                {lang === "en" ? "Overall status" : "Statut global"}
              </p>
              <h2 className="display text-2xl md:text-4xl mt-2 leading-tight">{overallLabel}</h2>
            </div>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              data-testid="status-refresh"
              className="btn-pill btn-ghost shrink-0 disabled:opacity-60"
            >
              <RefreshCcw size={14} className={loading ? "animate-spin" : ""} />
              {lang === "en" ? "Refresh" : "Actualiser"}
            </button>
          </div>

          {/* Service checks */}
          <div className="mt-12">
            <h3 className="display text-2xl mb-6">
              {lang === "en" ? "Components" : "Composants"}
            </h3>
            <div className="border border-ink/10 divide-y divide-ink/10 bg-paper">
              {data && Object.entries(data.checks || {}).map(([key, state]) => (
                <div
                  key={key}
                  data-testid={`status-check-${key}`}
                  className="px-6 py-5 flex items-center gap-5"
                >
                  {renderCheckIcon(state)}
                  <div className="flex-1">
                    <p className="display text-lg leading-tight">{LABELS[lang]?.[key] || key}</p>
                  </div>
                  <span
                    className="text-[12px] uppercase tracking-[0.2em] font-bold"
                    style={{
                      color:
                        state === "ok"
                          ? "#52A385"
                          : state === "not_configured"
                          ? "#F3A114"
                          : "#E62C86",
                    }}
                  >
                    {renderCheckLabel(state)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Last check info */}
          <div className="mt-10 border-t border-ink/10 pt-6 flex flex-wrap items-center justify-between gap-3 text-[12px] text-ink/70">
            <p>
              {lang === "en" ? "Last check:" : "Dernière vérification :"}{" "}
              <span className="text-ink/85 font-bold">{lastCheck ? fmtTime(lastCheck) : "—"}</span>
            </p>
            <p className="uppercase tracking-wider display italic">
              {lang === "en" ? "Auto-refresh every 60 seconds" : "Actualisation auto · 60 sec"}
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
}
